import React, { Fragment, useCallback, useEffect, useState } from "react";
import { ModeBadge } from "./ModeBadge";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, RefreshCw, Download, X, ChevronDown, ChevronRight, Clock, Zap, ZapOff,
} from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";

// 2026-09-28 — Momentum OI (Setup F) section (chat: "add new section
// 'Momentum OI'...don't include in Sniper"). Its own card on the Auto Picks
// tab, backed by /api/momentum-oi/*. Shape follows BearishPESetup.jsx (own
// header/collapse/scan-now/CSV, open + closed trade tables with an
// expandable detail row) plus Sniper's admin live-trading toggle.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v, d = 2) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d }));
const fmtPct = (v, d = 2) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  return `${n >= 0 ? "+" : ""}${fmt(n, d)}%`;
};
const pctColor = (v) => (v == null ? "var(--text-muted-2)" : v >= 0 ? "var(--bull)" : "var(--bear)");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", {
  timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
}) : "—");

const BUILDUP_LABEL = {
  LONG_BUILDUP: "Long BU", SHORT_BUILDUP: "Short BU",
  SHORT_COVERING: "Short cov", LONG_UNWINDING: "Long unw",
};
const BUILDUP_COLOR = {
  LONG_BUILDUP: "var(--bull)", SHORT_COVERING: "var(--bull)",
  SHORT_BUILDUP: "var(--bear)", LONG_UNWINDING: "var(--bear)",
};
const biasColor = (b) => (b === "BULL" ? "var(--bull)" : b === "BEAR" ? "var(--bear)" : "var(--text-muted-2)");

const Top10Table = ({ rows }) => (
  <div className="border border-grid overflow-x-auto" data-testid="momentum-oi-top10">
    {!rows || rows.length === 0 ? (
      <div className="px-4 py-6 text-center text-xs text-muted-2 font-mono-data">
        No stock passed the momentum + volume filters in this run.
      </div>
    ) : (
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid">
            <th className="px-3 py-2">Symbol</th>
            <th className="px-3 py-2 text-right">%Chg</th>
            <th className="px-3 py-2 text-right">RVOL (so far / proj)</th>
            <th className="px-3 py-2 text-center">VWAP</th>
            <th className="px-3 py-2 text-right">Fut OI %</th>
            <th className="px-3 py-2">Buildup</th>
            <th className="px-3 py-2">Bias</th>
            <th className="px-3 py-2 text-right">Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const vw = r.vwap == null ? "–" : r.ltp > r.vwap ? "▲" : "▼";
            const vwColor = r.vwap == null ? "var(--text-muted-2)" : r.ltp > r.vwap ? "var(--bull)" : "var(--bear)";
            return (
              <tr key={r.symbol} className="border-b border-grid/40 hover:bg-white/[0.02]"
                  title={(r.notes || []).join("\n") || undefined}>
                <td className="px-3 py-2 font-semibold">{r.symbol}</td>
                <td className="px-3 py-2 text-right" style={{ color: pctColor(r.chg_pct) }}>{fmtPct(r.chg_pct)}</td>
                <td className="px-3 py-2 text-right text-muted-2">
                  {r.rvol != null ? `${fmt(r.rvol, 2)}×` : "—"} / {r.rvol_proj != null ? `${fmt(r.rvol_proj, 1)}×` : "—"}
                </td>
                <td className="px-3 py-2 text-center" style={{ color: vwColor }}>{vw}</td>
                <td className="px-3 py-2 text-right" style={{ color: pctColor(r.fut_oi_chg_pct) }}>
                  {r.fut_oi_chg_pct != null ? fmtPct(r.fut_oi_chg_pct, 1) : "—"}
                </td>
                <td className="px-3 py-2" style={{ color: BUILDUP_COLOR[r.buildup] || "var(--text-muted-2)" }}>
                  {BUILDUP_LABEL[r.buildup] || "—"}
                </td>
                <td className="px-3 py-2 font-mono-data" style={{ color: biasColor(r.bias) }}>{r.bias || "—"}</td>
                <td className="px-3 py-2 text-right text-muted-2">{r.score ? fmt(r.score, 2) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    )}
  </div>
);

const SetupCard = ({ s }) => {
  const o = s.option || {};
  const st = s.stock || {};
  const bear = st.bias === "BEAR";
  const color = bear ? "var(--bear)" : "var(--bull)";
  return (
    <div className="border-grid surface flex flex-col" data-testid={`momentum-oi-setup-${s.rank}`}>
      <div className="px-4 py-3 border-b border-grid flex items-center justify-between gap-2">
        <div>
          <div className="font-heading text-sm font-semibold">#{s.rank} {o.trading_symbol || `${st.symbol} ${o.strike} ${o.opt_type}`}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">exp {o.expiry}</div>
        </div>
        <div className="flex items-center gap-1.5">
          {o.opposite_writing && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border font-mono-data"
                  style={{ color: "#FFB66E", borderColor: "#FFB66E" }}
                  title="Opposite-side ATM option: OI up and premium down (writers capping the move)">
              Opp. writing
            </span>
          )}
          <span className="text-[10px] uppercase tracking-wider px-2 py-1 border font-mono-data" style={{ color, borderColor: color }}>
            {s.signal_type}
          </span>
        </div>
      </div>
      <div className="px-4 py-2.5 border-b border-grid grid grid-cols-2 md:grid-cols-4 gap-2 font-mono-data text-[11px]">
        <div><div className="text-[10px] uppercase tracking-wider text-muted-3">Entry</div>
          <div className="font-semibold text-xs mt-0.5">₹{fmt(s.entry_low)} – {fmt(s.entry_high)}</div>
          <div className="text-[10px] text-muted-3">LTP ₹{fmt(o.ltp)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-3">T1 / T2</div>
          <div className="font-semibold text-xs mt-0.5" style={{ color: "var(--bull)" }}>₹{fmt(s.t1_premium)} / ₹{fmt(s.t2_premium)}</div>
          <div className="text-[10px] text-muted-3">spot {fmt(s.t1_spot)} / {fmt(s.t2_spot)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-3">SL</div>
          <div className="font-semibold text-xs mt-0.5" style={{ color: "var(--bear)" }}>₹{fmt(s.sl_premium)}</div>
          <div className="text-[10px] text-muted-3">or 15m close beyond {fmt(s.sl_spot)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-3">Lot</div>
          <div className="font-semibold text-xs mt-0.5">{o.lot_size ?? "—"}</div>
          <div className="text-[10px] text-muted-3">≈ ₹{fmt(s.premium_per_lot, 0)}</div></div>
      </div>
      <div className="px-4 py-2 text-[11px] text-muted-2 leading-snug">{s.rationale}</div>
    </div>
  );
};

const TradeDetailRow = ({ t, colSpan }) => (
  <tr className="border-b border-grid/40" style={{ background: "rgba(255,255,255,0.015)" }}>
    <td colSpan={colSpan} className="px-3 py-2.5">
      <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono-data text-[11px] text-muted-2 pl-9">
        <span>SL <span className="text-muted-1">₹{fmt(t.current_sl_premium ?? t.sl_premium)}</span>
          {t.t1_hit && <span style={{ color: "var(--bull)" }}> (moved to entry after T1)</span>}</span>
        <span>Spot SL (15m close) <span className="text-muted-1">{fmt(t.sl_spot)}</span></span>
        {t.exit_plan === "FIXED_PCT" ? (
          <span>Target <span className="text-muted-1">₹{fmt(t.t2_premium)}</span>
            {t.target_pct != null && <span> (+{fmt(t.target_pct, 1)}% / SL {fmt(t.stop_loss_pct, 1)}%)</span>}</span>
        ) : (
          <span>T1 / T2 <span className="text-muted-1">₹{fmt(t.t1_premium)} / ₹{fmt(t.t2_premium)}</span></span>
        )}
        <span>Lot <span className="text-muted-1">{t.atm_lot_size ?? "—"}</span> × {t.lots_placed ?? 1}</span>
        <span>RVOL <span className="text-muted-1">{t.rvol != null ? `${fmt(t.rvol, 2)}×` : "—"} / {t.rvol_proj != null ? `${fmt(t.rvol_proj, 1)}×` : "—"}</span></span>
        <span>Fut OI <span style={{ color: pctColor(t.fut_oi_chg_pct) }}>{fmtPct(t.fut_oi_chg_pct, 1)}</span> {BUILDUP_LABEL[t.buildup] || ""}</span>
        <span>Delta <span className="text-muted-1">{fmt(t.delta_at_entry, 2)}</span></span>
        <span>Expiry <span className="text-muted-1">{t.expiry || "—"}</span></span>
        <span>Mode <span className="text-muted-1">{t.live_trade ? "LIVE" : "Paper"}</span>
          {!t.live_trade && t.live_skip_reason && t.live_skip_reason !== "live_trading_off" &&
            <span className="text-muted-3"> ({t.live_skip_reason})</span>}</span>
        {t.live_trade && (
          <span>Broker <span className="text-muted-1">{t.live_broker_status || "—"}</span>
            {t.live_avg_fill_price ? ` @ ₹${fmt(t.live_avg_fill_price)}` : ""}</span>
        )}
        {t.planned_entry != null && t.planned_entry !== t.atm_ce_ltp && (
          <span>Planned entry <span className="text-muted-1">₹{fmt(t.planned_entry)}</span>
            {t.entry_slippage_pct != null && (
              <span style={{ color: pctColor(-t.entry_slippage_pct) }}> (slippage {fmtPct(t.entry_slippage_pct)})</span>
            )}</span>
        )}
        {t.rg_shadow_block && (
          <span>Risk-gov (shadow) <span style={{ color: "#FFB66E" }}>would block: {t.rg_shadow_block.reason}</span></span>
        )}
        {t.entry_error && <span>Entry error <span style={{ color: "var(--bear)" }}>{t.entry_error}</span></span>}
        {t.status === "CLOSED" && (
          <>
            <span>Exit <span className="text-muted-1">{t.exit_trigger || t.close_reason}</span></span>
            <span>Net P&amp;L <span style={{ color: pctColor(t.net_pnl) }}>{fmt(t.net_pnl)}</span></span>
          </>
        )}
      </div>
      {t.rationale && <div className="pl-9 pt-1.5 text-[11px] text-muted-3 leading-snug">{t.rationale}</div>}
    </td>
  </tr>
);

const TradeRow = ({ t, onExit, colSpan }) => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <tr className="border-b border-grid/40 hover:bg-white/[0.02]" data-testid="momentum-oi-trade-row">
        <td className="px-3 py-2.5">
          <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2">
            <span className="text-muted-3">{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
            <span className="font-semibold">{t.symbol}</span>
            <ModeBadge t={t} className="" />
          </button>
        </td>
        <td className="px-3 py-2.5" style={{ color: t.direction === "BEAR" ? "var(--bear)" : "var(--bull)" }}>
          {t.option_type} {fmt(t.atm_strike, 2)}
        </td>
        <td className="px-3 py-2.5 text-right text-muted-2">₹{fmt(t.atm_ce_ltp)}</td>
        <td className="px-3 py-2.5 text-right text-muted-2">₹{fmt(t.status === "OPEN" ? t.current_ce_ltp : t.close_ce_ltp)}</td>
        <td className="px-3 py-2.5 text-right font-semibold" style={{ color: pctColor(t.pnl_pct) }}>{fmtPct(t.pnl_pct)}</td>
        <td className="px-3 py-2.5 text-muted-3 font-mono-data text-[10px]">{fmtDateTime(t.entered_at)}</td>
        <td className="px-3 py-2.5 text-muted-3 font-mono-data text-[10px]">{t.status === "CLOSED" ? fmtDateTime(t.closed_at) : "—"}</td>
        <td className="px-3 py-2.5 text-right">
          {t.status === "PENDING_ENTRY" ? (
            <span className="text-[10px] text-muted-3 font-mono-data uppercase">Entering…</span>
          ) : t.status === "OPEN" && onExit ? (
            <button onClick={() => onExit(t)} data-testid={`momentum-oi-manual-exit-${t.id}`}
              className="px-2 py-1 border text-[10px] uppercase tracking-wider font-mono-data flex items-center gap-1 ml-auto"
              style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
              <X size={10} /> Exit
            </button>
          ) : (
            <span className="text-[10px] text-muted-3 font-mono-data uppercase">
              {t.status === "ENTRY_ERROR" ? "ENTRY_ERROR" : (t.close_reason || (t.status === "OPEN" ? "Open" : "—"))}
            </span>
          )}
        </td>
      </tr>
      {open && <TradeDetailRow t={t} colSpan={colSpan} />}
    </Fragment>
  );
};

const TradeTable = ({ trades, onExit, emptyLabel, testid, open }) => (
  <div className="border border-grid overflow-x-auto" data-testid={testid}>
    {!trades || trades.length === 0 ? (
      <div className="px-4 py-6 text-center text-xs text-muted-2 font-mono-data">{emptyLabel}</div>
    ) : (
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid">
            <th className="px-3 py-2">Symbol</th>
            <th className="px-3 py-2">Leg</th>
            <th className="px-3 py-2 text-right">Entry</th>
            <th className="px-3 py-2 text-right">{open ? "Current" : "Exit"}</th>
            <th className="px-3 py-2 text-right">P&amp;L%</th>
            <th className="px-3 py-2">Entered</th>
            <th className="px-3 py-2">Exited</th>
            <th className="px-3 py-2 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => <TradeRow key={t.id} t={t} onExit={onExit} colSpan={8} />)}
        </tbody>
      </table>
    )}
  </div>
);

export const MomentumOISetup = ({ isAdmin = false }) => {
  const [data, setData] = useState(null);
  const [closedTrades, setClosedTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [liveBusy, setLiveBusy] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const [collapsed, toggleCollapsed] = useCollapsed("momentum-oi", false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/momentum-oi`);
      setData(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const loadClosed = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/momentum-oi/paper-trades`);
      const all = [...(r.data?.today || []), ...(r.data?.earlier || [])];
      setClosedTrades(all.filter((t) => t.status !== "OPEN" && t.status !== "PENDING_ENTRY"));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { if (showClosed) loadClosed(); }, [showClosed, loadClosed]);

  const runs = data?.runs || [];
  const run = runs.find((r) => r.id === selectedRunId) || runs[0] || null;
  const cfg = data?.config || {};
  const openTrades = data?.open_trades || [];
  const liveEnabled = !!data?.live_trading_enabled;

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/momentum-oi/scan`);
      const res = r.data?.run || {};
      if (res.status && res.status !== "SUCCESS") {
        toast.error(`Momentum OI scan ${res.status}${res.error ? ` · ${res.error}` : ""}`);
      } else {
        const n = (res.setups || []).length;
        toast.success(`Momentum OI scan · ${res.momentum_count ?? 0} movers · ${n} setup${n === 1 ? "" : "s"}`);
      }
      setSelectedRunId(null);
      await load();
    } catch (e) {
      if (e?.response?.status === 409) toast.error("A Momentum OI scan is already running — try again shortly");
      else if (e?.response?.status === 403 || e?.response?.status === 401) toast.error("Admin only");
      else toast.error("Scan failed");
    }
    finally { setScanning(false); }
  };

  const onExit = async (t) => {
    if (!window.confirm(`Exit ${t.symbol}?${t.live_trade ? "\n\nThis is a LIVE trade — a SELL will be placed on Upstox (only if the position exists)." : ""}`)) return;
    try {
      await axios.post(`${API}/momentum-oi/paper-trades/manual-exit`, { trade_id: t.id });
      toast.success(`${t.symbol} closed`);
      await load();
      if (showClosed) await loadClosed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Exit failed");
    }
  };

  const onToggleLive = async () => {
    const next = !liveEnabled;
    if (!window.confirm(
      `${next ? "ENABLE" : "DISABLE"} LIVE trading for Momentum OI?\n\n` +
      (next
        ? `New entries will place real Upstox BUY orders (max ${cfg.max_live_trades_per_day ?? 2}/day, ` +
          `max ${cfg.max_open_live_trades ?? 2} open, ${cfg.lots_per_trade ?? 1} lot).`
        : "New entries will be paper only. Open live positions still exit on SL/T2/15:10/manual exit.")
    )) return;
    setLiveBusy(true);
    try {
      await axios.post(`${API}/momentum-oi/live-status`, { enabled: next });
      toast.success(next ? "Momentum OI LIVE trading ON" : "Momentum OI LIVE trading OFF");
      await load();
    } catch { toast.error("Failed to update live-trading status"); }
    finally { setLiveBusy(false); }
  };

  return (
    <section className="space-y-4" data-testid="momentum-oi-section">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <CollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} testId="momentum-oi-collapse" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
              AUTO-PILOT · MOMENTUM OI
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
              <Activity size={20} style={{ color: "var(--bull)" }} />
              Momentum OI
            </h2>
            <p className="text-xs text-muted-2 mt-1 max-w-3xl" data-testid="momentum-oi-description">
              Nifty 50 + liquid F&amp;O stocks moving ≥{fmt(cfg.min_abs_change_pct ?? 1.5, 1)}%, ranked by volume
              surge vs their 5-day average, confirmed by VWAP, 15m Supertrend and near+next futures OI buildup
              (≥{fmt(cfg.min_fut_oi_change_pct ?? 3, 1)}%). Picks an ATM / first-OTM option that shows its own long
              buildup. Scans every <strong>{data?.scan_interval_min ?? 5} min, {data?.scan_window_ist || "09:30-12:00"} IST</strong>;
              top {cfg.top_setups ?? 2} setups per run become trades (one per symbol per day) only if they pass the
              entry gates (projected RVOL, fresh session low/high, daily loss limit; Nifty 15m alignment is optional, off by default).
              {Number(cfg.use_fixed_pct_exits ?? 1) === 1
                ? <> Exits: target +{fmt(cfg.target_pct ?? 8, 1)}% / stop −{fmt(cfg.stop_loss_pct ?? 7, 1)}% of the entry
                  premium, 15m close beyond spot SL, time exit {data?.time_exit || "15:10"}.</>
                : <> Exits: premium SL or 15m close beyond spot SL, T1 moves SL to entry, T2 target, time
                  exit {data?.time_exit || "15:10"}.</>}
            </p>
          </div>
        </div>
        {!collapsed && (
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin ? (
              <button onClick={onToggleLive} disabled={liveBusy} data-testid="momentum-oi-live-toggle"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono-data text-xs uppercase hover:bg-white/5"
                style={{ color: liveEnabled ? "var(--bull)" : "var(--text-muted-2)", borderColor: liveEnabled ? "var(--bull)" : "var(--grid)" }}>
                {liveEnabled ? <Zap size={12} /> : <ZapOff size={12} />}
                {liveEnabled ? "Live ON" : "Live OFF (paper)"}
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono-data text-xs uppercase"
                    style={{ color: liveEnabled ? "var(--bull)" : "var(--text-muted-2)", borderColor: "var(--grid)" }}>
                {liveEnabled ? <Zap size={12} /> : <ZapOff size={12} />} {liveEnabled ? "Live" : "Paper"}
              </span>
            )}
            {isAdmin && (
              <button onClick={runScan} disabled={scanning} data-testid="momentum-oi-scan-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-grid font-mono-data text-xs uppercase hover:bg-white/5">
                <RefreshCw size={12} className={scanning ? "animate-spin" : ""} />
                {scanning ? "Scanning…" : "Scan Now"}
              </button>
            )}
            <a href={`${API}/momentum-oi/export.csv`} data-testid="momentum-oi-export-csv"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-grid font-mono-data text-xs uppercase hover:bg-white/5">
              <Download size={12} /> Export CSV
            </a>
          </div>
        )}
      </div>

      {!collapsed && (
        loading && !data ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 font-mono-data text-xs">
              <div className="border-grid surface px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-3">Nifty</div>
                <div className="text-lg font-semibold mt-1" style={{ color: pctColor(run?.nifty_chg_pct) }}>{fmtPct(run?.nifty_chg_pct)}</div>
              </div>
              <div className="border-grid surface px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-3">Movers ≥{fmt(cfg.min_abs_change_pct ?? 1.5, 1)}%</div>
                <div className="text-lg font-semibold mt-1">{run?.momentum_count ?? "—"} / {run?.universe_size ?? "—"}</div>
              </div>
              <div className="border-grid surface px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-3">Open Trades</div>
                <div className="text-lg font-semibold mt-1">{data?.open_count ?? 0}</div>
              </div>
              <div className="border-grid surface px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-3">Live Today</div>
                <div className="text-lg font-semibold mt-1">{data?.live_trades_today ?? 0} / {cfg.max_live_trades_per_day ?? 2}</div>
              </div>
              <div className="border-grid surface px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-3">Next Scan</div>
                <div className="text-sm font-semibold mt-1.5 flex items-center gap-1">
                  <Clock size={12} className="text-muted-3" /> {data?.next_scan_at || "—"}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-xs uppercase tracking-wider text-muted-3 font-mono-data">Run</h3>
              {runs.length > 0 ? (
                <select value={run?.id || ""} onChange={(e) => setSelectedRunId(e.target.value)}
                  data-testid="momentum-oi-run-select"
                  className="bg-transparent border border-grid px-2 py-1 text-xs font-mono-data">
                  {runs.map((r) => (
                    <option key={r.id} value={r.id}>
                      {(r.slot || "").replace(/^.*? (\d\d:\d\d).*$/, "$1") || r.ran_at_ist}
                      {r.manual ? " (manual)" : ""} · {r.status}{r.setups_count ? ` · ${r.setups_count} setup(s)` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-xs text-muted-2 font-mono-data">No runs yet today — first slot {data?.slots?.[0] || "09:30"} IST.</span>
              )}
              {run && run.status !== "SUCCESS" && (
                <span className="text-xs font-mono-data" style={{ color: "var(--bear)" }}>{run.status}: {run.error}</span>
              )}
              {run?.entries_skipped && (
                <span className="text-[11px] text-muted-3 font-mono-data">entries skipped: {run.entries_skipped}</span>
              )}
            </div>

            {run && run.status === "SUCCESS" && (
              <>
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-muted-3 font-mono-data mb-2">
                    Top by volume surge ({(run.top10 || []).length})
                  </h3>
                  <Top10Table rows={run.deep_dive?.length ? mergeTop10(run.top10, run.deep_dive) : run.top10} />
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-muted-3 font-mono-data mb-2">
                    Setups ({(run.setups || []).length})
                  </h3>
                  {(run.setups || []).length === 0 ? (
                    <div className="border border-grid px-4 py-4 text-xs text-muted-2 font-mono-data">
                      No setup passed all filters this run.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {run.setups.map((s) => <SetupCard key={`${s.rank}-${s.option?.instrument_key}`} s={s} />)}
                    </div>
                  )}
                </div>
              </>
            )}

            <div>
              <h3 className="text-xs uppercase tracking-wider text-muted-3 font-mono-data mb-2">Open Trades</h3>
              <TradeTable trades={openTrades} onExit={isAdmin ? onExit : null} open testid="momentum-oi-open-trades"
                emptyLabel="No open Momentum OI trades." />
            </div>

            <div>
              <button onClick={() => setShowClosed((v) => !v)} data-testid="momentum-oi-toggle-closed"
                className="text-xs uppercase tracking-wider text-muted-3 font-mono-data mb-2 hover:text-muted-1 flex items-center gap-1">
                {showClosed ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Closed Trades
              </button>
              {showClosed && (
                <TradeTable trades={closedTrades} testid="momentum-oi-closed-trades" emptyLabel="No closed trades yet." />
              )}
            </div>
            <ShadowOutcomeCard />
          </>
        )
      )}
    </section>
  );
};

// 2026-10-17 — LOG ONLY (backend momentum_oi_shadow.py, runs 15:45): what the
// setups each gate skipped would have done with the same fixed exits, and how
// real trades moved after entry (whipsaw = stopped, then hit the target later).
const ShadowOutcomeCard = () => {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/momentum-oi/shadow-summary`, { params: { days } })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [days]);
  if (!d) return null;
  const st = (s) => (s?.trades ? `${s.trades} · ${fmt(s.win_rate_pct, 1)}% · avg ${fmtPct(s.avg_pnl_pct)} · PF ${s.profit_factor ?? "—"}` : "—");
  return (
    <div className="border border-grid p-3 space-y-2" data-testid="momentum-oi-shadow-card">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wider text-muted-3 font-mono-data">Outcome tracker (log only)</div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                className="bg-transparent border border-grid text-[10px] font-mono-data px-2 py-1">
          {[7, 14, 30, 60].map((n) => <option key={n} value={n}>{n}d</option>)}
        </select>
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        Skipped setups are replayed on real 1-min option prices with the same target/stop from the price at the skip
        (stop assumed first if both hit in one minute). Nothing is traded.
      </div>
      <table className="w-full text-[11px] font-mono-data">
        <tbody>
          <tr className="border-b border-grid/40"><td className="px-2 py-1 text-muted-2">Real trades</td><td className="px-2 py-1">{st(d.real)}</td></tr>
          <tr className="border-b border-grid/40"><td className="px-2 py-1 text-muted-2">All skipped (simulated)</td><td className="px-2 py-1">{st(d.skipped_all)}</td></tr>
          {Object.entries(d.skipped_by_reason || {}).map(([k, v]) => (
            <tr key={k} className="border-b border-grid/40"><td className="px-2 py-1 pl-5 text-muted-3">· {k}</td><td className="px-2 py-1">{st(v)}</td></tr>
          ))}
          <tr className="border-b border-grid/40">
            <td className="px-2 py-1 text-muted-2">Stop-outs that later hit target</td>
            <td className="px-2 py-1">{d.stops ? `${d.whipsaw_stops} of ${d.stops} (${fmt(d.whipsaw_rate_pct, 1)}%)` : "—"}</td>
          </tr>
          <tr>
            <td className="px-2 py-1 text-muted-2">Avg best / worst in 30 min</td>
            <td className="px-2 py-1">{fmtPct(d.avg_mfe_30m_pct)} / {fmtPct(d.avg_mae_30m_pct)}</td>
          </tr>
        </tbody>
      </table>
      {/* 2026-10-27 — same entries under other exit rules (log only) */}
      {d.exit_variants && (
        <table className="w-full text-[11px] font-mono-data" data-testid="momentum-oi-exit-variants">
          <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
            <tr className="border-b border-grid">
              <th className="text-left px-2 py-1 font-normal">Exit rule (study)</th>
              <th className="text-left px-2 py-1 font-normal">Real trades</th>
              <th className="text-left px-2 py-1 font-normal">Skipped (excl. too far)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(d.exit_variants).map(([k, v]) => (
              <tr key={k} className="border-b border-grid/40">
                <td className="px-2 py-1 text-muted-2">{({ t8_s7: "Target 8 / stop 7 (current)", t12_s10: "Target 12 / stop 10",
                  t15_s12: "Target 15 / stop 12", t20_s15: "Target 20 / stop 15", t8_s7_15m: "8 / 7, flat after 15 min",
                  trail_be: "Stop to entry at +8, target 20" })[k] || k}</td>
                <td className="px-2 py-1">{st(v.real)}</td>
                <td className="px-2 py-1">{st(v.skipped)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(d.skips || []).length > 0 && (
        <>
          <button className="text-[10px] underline text-muted-2 font-mono-data" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Show"} skipped setups
          </button>
          {open && (
            <table className="w-full text-[11px] font-mono-data">
              <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
                <tr className="border-b border-grid">
                  {["Day", "Time", "Symbol", "Reason", "Entry", "Outcome", "P&L", "Best 30m"].map((h) => (
                    <th key={h} className="text-left px-2 py-1 font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.skips.map((r, i) => (
                  <tr key={i} className="border-b border-grid/40">
                    <td className="px-2 py-1">{r.day}</td>
                    <td className="px-2 py-1">{r.t}</td>
                    <td className="px-2 py-1 font-semibold">{r.symbol}</td>
                    <td className="px-2 py-1 text-muted-2">{r.reason}</td>
                    <td className="px-2 py-1">{fmt(r.entry)}</td>
                    <td className="px-2 py-1">{r.outcome}{r.exit_t ? ` ${r.exit_t}` : ""}</td>
                    <td className="px-2 py-1" style={{ color: pctColor(r.pnl_pct) }}>{fmtPct(r.pnl_pct)}</td>
                    <td className="px-2 py-1">{fmtPct(r.mfe_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

// deep_dive rows carry fut OI / buildup / bias / score for the top K; merge
// them onto the top-10 list so those columns fill in where computed.
function mergeTop10(top10 = [], deep = []) {
  const bySym = Object.fromEntries(deep.map((d) => [d.symbol, d]));
  return top10.map((r) => ({ ...r, ...(bySym[r.symbol] || {}) }));
}

export default MomentumOISetup;
